/**
 * E-Ticket Generator Utility
 * Generates PDF e-tickets using HTML-to-PDF conversion
 * Uses native browser APIs (no external libraries)
 */

export const generateETicketPDF = (eTicketData, fileName = 'e-ticket.pdf') => {
  try {
    // Create HTML content for the e-ticket
    const htmlContent = createETicketHTML(eTicketData);

    // Open a new window to display the e-ticket (no auto-print)
    const printWindow = window.open('', '', 'width=900,height=1000');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${fileName}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Arial', sans-serif;
            background: #f5f5f5;
            padding: 20px;
            line-height: 1.6;
          }
          
          .toolbar {
            background: white;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            display: flex;
            gap: 10px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            position: sticky;
            top: 0;
            z-index: 100;
          }
          
          .toolbar button {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .btn-print {
            background: #4CAF50;
            color: white;
          }
          
          .btn-print:hover {
            background: #45a049;
          }
          
          .btn-download {
            background: #2196F3;
            color: white;
          }
          
          .btn-download:hover {
            background: #0b7dda;
          }
          
          .btn-close {
            background: #f44336;
            color: white;
            margin-left: auto;
          }
          
          .btn-close:hover {
            background: #da190b;
          }
          
          .preview-container {
            background: white;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            max-width: 900px;
            margin: 0 auto;
          }
          
          @page {
            size: A4;
            margin: 0;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .toolbar {
              display: none;
            }
            
            .preview-container {
              box-shadow: none;
              padding: 0;
              max-width: 100%;
            }
          }
          
          .eticket-container {
            max-width: 210mm;
            background: white;
            border: 2px solid #333;
            padding: 20px;
            margin: 0 auto;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px dashed #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          
          .header h1 {
            font-size: 28px;
            color: #1a1a1a;
            margin-bottom: 5px;
          }
          
          .header p {
            font-size: 12px;
            color: #666;
          }
          
          .ticket-number {
            font-size: 14px;
            font-weight: bold;
            color: #d4af37;
            margin-top: 10px;
          }
          
          .section {
            margin-bottom: 20px;
            padding: 15px;
            background: #f9f9f9;
            border-left: 4px solid #d4af37;
          }
          
          .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #1a1a1a;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .flight-info {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 20px;
            margin-bottom: 15px;
          }
          
          .flight-leg {
            text-align: center;
          }
          
          .airport-code {
            font-size: 24px;
            font-weight: bold;
            color: #d4af37;
            margin-bottom: 5px;
          }
          
          .airport-name {
            font-size: 11px;
            color: #666;
            margin-bottom: 8px;
          }
          
          .flight-time {
            font-size: 14px;
            font-weight: bold;
            color: #1a1a1a;
            margin-bottom: 3px;
          }
          
          .flight-date {
            font-size: 11px;
            color: #666;
          }
          
          .arrow {
            font-size: 20px;
            color: #d4af37;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .passenger-list {
            margin-top: 10px;
          }
          
          .passenger-item {
            padding: 8px 0;
            border-bottom: 1px solid #ddd;
            font-size: 12px;
          }
          
          .passenger-item:last-child {
            border-bottom: none;
          }
          
          .passenger-name {
            font-weight: bold;
            color: #1a1a1a;
          }
          
          .passenger-details {
            font-size: 11px;
            color: #666;
            margin-top: 3px;
          }
          
          .booking-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            font-size: 12px;
          }
          
          .detail-item {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
          }
          
          .detail-label {
            font-weight: bold;
            color: #1a1a1a;
          }
          
          .detail-value {
            color: #666;
          }
          
          .footer {
            text-align: center;
            border-top: 3px dashed #333;
            padding-top: 15px;
            margin-top: 20px;
            font-size: 10px;
            color: #999;
          }
          
          .status-badge {
            display: inline-block;
            padding: 5px 10px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
            margin-top: 5px;
          }
          
          .status-confirmed {
            background: #d4f1d4;
            color: #2d5016;
          }
          
          .status-pending {
            background: #fff3cd;
            color: #856404;
          }
          
          .amendment-info {
            background: #e7f3ff;
            border-left: 4px solid #0066cc;
            padding: 10px;
            margin-top: 10px;
            font-size: 11px;
          }
          
          .amendment-info strong {
            color: #0066cc;
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button class="btn-print" onclick="window.print()">🖨️ Print</button>
          <button class="btn-download" onclick="downloadPDF()">⬇️ Download as PDF</button>
          <button class="btn-close" onclick="window.close()">✕ Close</button>
        </div>
        <div class="preview-container">
          ${htmlContent}
        </div>
        <script>
          function downloadPDF() {
            // Trigger browser's print-to-PDF
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();

    // Clean up - no auto-print, just display
    return true;
  } catch (error) {
    console.error('Error generating e-ticket PDF:', error);
    throw error;
  }
};

/**
 * Create HTML content for e-ticket
 */
const createETicketHTML = (data) => {
  const {
    booking_reference,
    e_ticket_number,
    version_number,
    booking_date,
    amendment_date,
    flight,
    passengers,
    booking_status,
    payment_status,
    total_amount,
    currency,
    passenger_count,
    amendment_reason,
    amended_by_role,
    approval_status,
    route_type,
    notes
  } = data;

  const departureTime = new Date(flight.departure.datetime);
  const arrivalTime = new Date(flight.arrival.datetime);
  const departureHours = departureTime.getHours().toString().padStart(2, '0');
  const departureMinutes = departureTime.getMinutes().toString().padStart(2, '0');
  const arrivalHours = arrivalTime.getHours().toString().padStart(2, '0');
  const arrivalMinutes = arrivalTime.getMinutes().toString().padStart(2, '0');

  const departureDateStr = departureTime.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const arrivalDateStr = arrivalTime.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const bookingDateStr = new Date(booking_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const amendmentDateStr = new Date(amendment_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const statusClass = booking_status === 'Confirmed' ? 'status-confirmed' : 'status-pending';
  const statusText = booking_status === 'Confirmed' ? 'CONFIRMED' : booking_status.toUpperCase();

  return `
    <div class="eticket-container">
      <div class="header">
        <h1>E-TICKET</h1>
        <p>Electronic Ticket - Valid for travel</p>
        <div class="ticket-number">Ticket #: ${e_ticket_number}</div>
        <div class="ticket-number">Booking Ref: ${booking_reference}</div>
      </div>

      <div class="section">
        <div class="section-title">Flight Information</div>
        <div class="flight-info">
          <div class="flight-leg">
            <div class="airport-code">${flight.departure.iata}</div>
            <div class="airport-name">${flight.departure.airport}</div>
            <div class="flight-time">${departureHours}:${departureMinutes}</div>
            <div class="flight-date">${departureDateStr}</div>
          </div>
          <div class="arrow">→</div>
          <div class="flight-leg">
            <div class="airport-code">${flight.arrival.iata}</div>
            <div class="airport-name">${flight.arrival.airport}</div>
            <div class="flight-time">${arrivalHours}:${arrivalMinutes}</div>
            <div class="flight-date">${arrivalDateStr}</div>
          </div>
        </div>
        <div style="text-align: center; font-size: 12px; color: #666; margin-top: 10px;">
          <strong>Flight:</strong> ${flight.flight_number} | 
          <strong>Aircraft:</strong> ${flight.aircraft_name} (${flight.registration_number})
        </div>
      </div>

      <div class="section">
        <div class="section-title">Passengers (${passenger_count})</div>
        <div class="passenger-list">
          ${passengers.map((p, idx) => `
            <div class="passenger-item">
              <div class="passenger-name">${idx + 1}. ${p.full_name.toUpperCase()}</div>
              <div class="passenger-details">
                DOB: ${new Date(p.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} | 
                Gender: ${p.gender || 'N/A'} | 
                Email: ${p.email}
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <div class="section">
        <div class="section-title">Booking Details</div>
        <div class="booking-details">
          <div class="detail-item">
            <span class="detail-label">Booking Date:</span>
            <span class="detail-value">${bookingDateStr}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Status:</span>
            <span class="detail-value">
              <span class="status-badge ${statusClass}">${statusText}</span>
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Total Amount:</span>
            <span class="detail-value">${currency} ${parseFloat(total_amount).toFixed(2)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Payment Status:</span>
            <span class="detail-value">${payment_status}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Route Type:</span>
            <span class="detail-value">${route_type}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Passengers:</span>
            <span class="detail-value">${passenger_count}</span>
          </div>
        </div>
      </div>

      ${version_number > 1 ? `
        <div class="amendment-info">
          <strong>Amendment Information (Version ${version_number})</strong><br>
          <strong>Amended:</strong> ${amendmentDateStr}<br>
          <strong>Reason:</strong> ${amendment_reason}<br>
          <strong>By:</strong> ${amended_by_role} | <strong>Status:</strong> ${approval_status}
        </div>
      ` : ''}

      ${notes ? `
        <div class="section">
          <div class="section-title">Notes</div>
          <p style="font-size: 12px; color: #666;">${notes}</p>
        </div>
      ` : ''}

      <div class="footer">
        <p>This is an electronic ticket. Please present this document at check-in.</p>
        <p>Generated on: ${new Date().toLocaleString('en-US')}</p>
        <p>© JetScanner 360 - All Rights Reserved</p>
      </div>
    </div>
  `;
};

/**
 * Download e-ticket as PDF using print-to-PDF
 */
export const downloadETicketPDF = (eTicketData, fileName = 'e-ticket.pdf') => {
  generateETicketPDF(eTicketData, fileName);
};

/**
 * Send e-ticket to email via backend
 */
export const sendETicketToEmail = async (bookingToken, versionId, apiBase) => {
  try {
    const response = await fetch(
      `${apiBase}/bookings/${bookingToken}/send-e-ticket/${versionId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending e-ticket to email:', error);
    throw error;
  }
};
